import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { SignUpHeader } from '../../components/SignUpHeader';
import { PrimaryButton } from '../../components/PrimaryButton';
import {
  DocumentUploader,
  emptyPending,
  pendingFromServer,
  type PendingDoc,
} from '../../components/DocumentUploader';
import {
  DocumentRequirement,
  ProfessionalDocument,
  getDocumentCatalog,
  getMyDocuments,
  uploadMyDocument,
  saveOnboardingStep,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { RequireVerifiedPro } from '../../../lib/use-auth';

export default function VerifyYourIdentityPage() {
  return (
    <RequireVerifiedPro>
      <VerifyIdentityInner />
    </RequireVerifiedPro>
  );
}

function VerifyIdentityInner() {
  const navigate = useNavigate();
  const location = useLocation();
  // Where to go after a successful save. When the pro arrives from
  // Account settings (editing their docs) we send them back there; the
  // fresh-signup funnel passes no `returnTo` and continues to the
  // onboarding wizard instead.
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ?? null;
  const [requirements, setRequirements] = useState<DocumentRequirement[]>([]);
  const [docs, setDocs] = useState<Record<string, ProfessionalDocument>>({});
  const [pending, setPending] = useState<Record<string, PendingDoc>>({});
  const [perRowError, setPerRowError] = useState<Record<string, string | null>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [cat, mine] = await Promise.all([
        getDocumentCatalog(),
        getMyDocuments(),
      ]);
      const reqs = cat.data ?? [];
      setRequirements(reqs);
      const docMap: Record<string, ProfessionalDocument> = {};
      for (const d of mine.data ?? []) {
        if (d.requirement?.id) docMap[d.requirement.id] = d;
      }
      setDocs(docMap);
      // Seed the local "pending" draft from whatever the backend
      // already has — so a returning customer doesn't lose previously
      // saved values, and so the upload cards correctly show their
      // existing thumbnails.
      const pendingMap: Record<string, PendingDoc> = {};
      for (const r of reqs) {
        pendingMap[r.id] = docMap[r.id]
          ? pendingFromServer(docMap[r.id])
          : emptyPending();
      }
      setPending(pendingMap);
      setPerRowError({});
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Could not load documents.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // No auto-redirect here — pros click "Verify Identity" from the
  // dashboard precisely to see what they already submitted. Bouncing
  // them onward to /service-offer (which itself bounces to /dashboard
  // for finished pros) would just round-trip them back where they
  // started. The submit handler still navigates forward on a fresh
  // save; revisiting just renders the saved state.

  const required = requirements.filter((r) => r.is_required);
  const optional = requirements.filter((r) => !r.is_required);

  const pendingFor = (r: DocumentRequirement): PendingDoc =>
    pending[r.id] ?? emptyPending();

  const docFor = (r: DocumentRequirement): ProfessionalDocument | null =>
    docs[r.id] ?? null;

  /**
   * Validates a single requirement against its draft. Returns either
   * a payload ready to POST, an "ok but nothing changed" sentinel,
   * or a human-readable error.
   */
  const validateForSubmit = (
    r: DocumentRequirement,
    p: PendingDoc,
    existing: ProfessionalDocument | null,
  ):
    | { kind: 'unchanged' }
    | { kind: 'payload'; payload: Record<string, unknown> }
    | { kind: 'error'; message: string } => {
    const hasFront = !!p.front_file_url;
    if (!hasFront) {
      // Optional + untouched → unchanged. Required + untouched →
      // surfaced as "still missing" by the bottom-button counter,
      // not as a per-row error.
      if (!r.is_required) return { kind: 'unchanged' };
      return { kind: 'unchanged' };
    }
    if (r.has_front_back && !p.back_file_url) {
      return {
        kind: 'error',
        message: 'Upload the back side before saving this document.',
      };
    }
    if (r.has_expiry && !p.expiry_date) {
      return {
        kind: 'error',
        message: 'Pick an expiry date before saving this document.',
      };
    }
    if (r.has_expiry && p.expiry_date && new Date(p.expiry_date).getTime() < Date.now()) {
      return {
        kind: 'error',
        message: 'Document has expired. Please select a future expiry date.',
      };
    }

    // Skip the POST when nothing changed vs the saved row.
    if (
      existing &&
      existing.front_file_url === p.front_file_url &&
      existing.back_file_url === p.back_file_url &&
      existing.expiry_date === p.expiry_date
    ) {
      return { kind: 'unchanged' };
    }

    return {
      kind: 'payload',
      payload: {
        requirement_id: r.id,
        front_file_url: p.front_file_url,
        back_file_url: p.back_file_url,
        mime_type: p.mime_type,
        file_size_bytes: p.file_size_bytes,
        original_filename: p.original_filename,
        expiry_date: p.expiry_date,
      },
    };
  };

  /**
   * Per-requirement breakdown of what's still missing on each required
   * draft. The page uses it to label the bottom button precisely so a
   * pro who's uploaded every file but skipped an expiry date doesn't
   * stare at a disabled "Save & continue" with no explanation.
   */
  const missingBreakdown = useMemo(() => {
    let missingFiles = 0;
    let missingExpiry = 0;
    for (const r of required) {
      const p = pendingFor(r);
      const hasFront = !!p.front_file_url;
      const hasBackIfNeeded = r.has_front_back ? !!p.back_file_url : true;
      const filesOk = hasFront && hasBackIfNeeded;
      const expiryOk = r.has_expiry ? !!p.expiry_date : true;
      if (!filesOk) missingFiles += 1;
      // A row with files uploaded but no expiry is purely an expiry
      // problem; we count it in its own bucket so the button can name
      // the actual blocker.
      if (filesOk && !expiryOk) missingExpiry += 1;
    }
    return { missingFiles, missingExpiry };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required, pending]);

  const missingRequiredCount =
    missingBreakdown.missingFiles + missingBreakdown.missingExpiry;

  const approvedCount = required.filter(
    (r) => docFor(r)?.review_status === 'APPROVED',
  ).length;
  const submittedCount = required.filter((r) => docFor(r) !== null).length;

  const allRequiredReady = missingRequiredCount === 0;

  const handleSubmitAll = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    // Walk every requirement (required + optional). Anything with a
    // complete draft and changes vs the saved row gets POSTed. We
    // run sequentially to keep per-row error reporting clean.
    const rowErrors: Record<string, string | null> = {};
    let hadAnyError = false;
    let savedAny = false;

    for (const r of requirements) {
      const p = pendingFor(r);
      const existing = docFor(r);
      const check = validateForSubmit(r, p, existing);
      if (check.kind === 'error') {
        rowErrors[r.id] = check.message;
        hadAnyError = true;
        continue;
      }
      if (check.kind === 'unchanged') {
        rowErrors[r.id] = null;
        continue;
      }
      try {
        await uploadMyDocument(check.payload as any);
        rowErrors[r.id] = null;
        savedAny = true;
      } catch (err) {
        rowErrors[r.id] = apiErrorMessage(err, 'Save failed.');
        hadAnyError = true;
      }
    }

    setPerRowError(rowErrors);
    if (hadAnyError) {
      setSubmitError(
        'One or more documents could not be saved. Check the highlighted cards and try again.',
      );
      setSubmitting(false);
      // Refresh the server state for any rows that *did* save.
      if (savedAny) await load();
      return;
    }

    // Refresh to pull the new review_status values, then progress.
    await load();
    setSubmitting(false);
    if (allRequiredReady) {
      // Identity docs done — mark that the pro has cleared verify-identity and
      // entered the wizard (onboarding_step 0 → 1). This is what makes a later
      // login route them back to /service-offer instead of here. Forward-only
      // on the server, so revisiting to review docs never rewinds real
      // progress. Fire-and-forget — must never block the continue.
      void saveOnboardingStep(1).catch(() => {});
      // Continue into the multi-step onboarding wizard (services → location →
      // certifications → payment). The "Verification complete" review screen
      // still lives at /professional/verification-complete and is reachable
      // from the dashboard / account-settings for pros checking review state.
      navigate(returnTo ?? '/professional/service-offer');
    }
  };

  // Pick the most informative blocker label. If files are missing, the
  // pro should upload first; if only expiry dates are missing, name
  // *that* so they know where to look on the cards above.
  const buttonLabel = (() => {
    if (submitting) return 'Saving…';
    if (allRequiredReady) return 'Save & continue';
    const { missingFiles, missingExpiry } = missingBreakdown;
    if (missingFiles > 0) {
      return `Upload ${missingFiles} more required document${
        missingFiles === 1 ? '' : 's'
      }`;
    }
    if (missingExpiry > 0) {
      return `Pick ${missingExpiry} more expiry date${
        missingExpiry === 1 ? '' : 's'
      }`;
    }
    return `Complete ${missingRequiredCount} more requirement${
      missingRequiredCount === 1 ? '' : 's'
    }`;
  })();

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SignUpHeader isProfessional={true} mode="signin" />

      <main className="flex-1 flex flex-col items-center py-6 md:py-12 px-4 w-full">
        <div className="max-w-[760px] w-full">
          <div className="mb-8 text-center">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px] mb-2">
              Verify your identity
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] max-w-[520px] mx-auto">
              Upload the documents below so we can verify you. Most reviews
              take 24-48 hours. Customers can only book you once every
              required document is approved.
            </p>
          </div>

          {required.length > 0 ? (
            <div className="rounded-xl bg-[#fafaf7] border border-[#ede8e9] p-4 mb-6 flex items-center justify-between gap-3">
              <div>
                <p className="text-[13px] font-bold text-[#3a3335]">Progress</p>
                <p className="text-[12px] text-[#908e8e]">
                  {approvedCount} of {required.length} approved ·{' '}
                  {submittedCount} of {required.length} submitted
                </p>
              </div>
              <div className="w-32 h-2 bg-[#ede8e9] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#8e2a2f] transition-all"
                  style={{
                    width: `${
                      required.length
                        ? (approvedCount / required.length) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          ) : null}

          {submitError ? (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {submitError}
            </div>
          ) : null}

          {loading ? (
            <div className="text-center text-[#908e8e] text-[14px] py-12">
              Loading documents…
            </div>
          ) : loadError ? (
            <div className="text-center text-[#991b1b] text-[14px] py-12">
              {loadError}
              <button
                type="button"
                onClick={load}
                className="block mx-auto mt-3 text-[#8e2a2f] font-bold hover:underline"
              >
                Retry
              </button>
            </div>
          ) : requirements.length === 0 ? (
            <div className="text-center text-[#908e8e] text-[14px] py-12">
              No document requirements have been configured. Contact support.
            </div>
          ) : (
            <div className="space-y-4">
              {required.map((r) => (
                <DocumentUploader
                  key={r.id}
                  requirement={r}
                  document={docFor(r)}
                  pending={pendingFor(r)}
                  onChange={(patch) =>
                    setPending((prev) => ({
                      ...prev,
                      [r.id]: { ...pendingFor(r), ...patch },
                    }))
                  }
                  submitError={perRowError[r.id] ?? null}
                />
              ))}

              {optional.length > 0 ? (
                <>
                  <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mt-8">
                    Optional documents
                  </h2>
                  {optional.map((r) => (
                    <DocumentUploader
                      key={r.id}
                      requirement={r}
                      document={docFor(r)}
                      pending={pendingFor(r)}
                      onChange={(patch) =>
                        setPending((prev) => ({
                          ...prev,
                          [r.id]: { ...pendingFor(r), ...patch },
                        }))
                      }
                      submitError={perRowError[r.id] ?? null}
                    />
                  ))}
                </>
              ) : null}

              <div className="pt-4 flex justify-center">
                <PrimaryButton
                  className="w-full max-w-[320px] py-4"
                  disabled={submitting || !allRequiredReady}
                  onClick={handleSubmitAll}
                >
                  {buttonLabel}
                </PrimaryButton>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
